import { type Field } from './field';
import { type WritableProxy } from '.';

export interface IForm<T extends Record<string | number, Field<boolean>>> {
    fields: T;
    error: boolean;
    invalid: boolean;
    dirty: boolean;
    reset: () => void;
    clear: () => void;
    validateAll: () => boolean;
    fieldCount: number;
    filledCount: number;
}

export class Form<T extends Record<string | number, Field<boolean>>>
    implements IForm<T>
{
    constructor(
        public fields: T,
        private model?: WritableProxy<Models<T> | undefined>
    ) {
        if (model) {
            Object.values(fields).forEach(field => {
                (field as any).onChange = () => this.updateModel();
            });
            watch(
                () => this.fieldsString,
                (newValue, oldValue) => {
                    const oldObject = JSON.parse(oldValue ?? '{}');
                    const newObject = JSON.parse(newValue);
                    for (const key in newObject) {
                        if (
                            newObject[key] !== oldObject[key] &&
                            key in fields &&
                            !fields[key].hidden
                        ) {
                            fields[key].model.value = (
                                fields[key] as any
                            ).config.reviver(newObject[key]);
                        }
                    }
                },
                { immediate: true }
            );
        }
    }

    public getData(): Models<T> {
        return Object.entries(this.fields).reduce(
            (previousValue, currentValue) => ({
                ...previousValue,
                [currentValue[0]]: currentValue[1].getData(),
            }),
            {}
        ) as any;
    }

    public setData(data: Partial<Models<T>>) {
        if (this.model?.value) {
            this.model.value = {
                ...this.model.value,
                ...data,
            };
        }
    }

    public updateModel() {
        if (this.model) {
            this.model.value = this.getData();
        }
    }

    public get fieldsString() {
        return JSON.stringify(this.model?.value || {});
    }

    public get error() {
        return Object.values(this.fields).some(field => field.error);
    }

    public get invalid() {
        return Object.values(this.fields).some(field => field.invalid);
    }

    public get dirty() {
        return Object.values(this.fields).some(field => field.dirty);
    }

    public reset() {
        Object.values(this.fields).forEach(field => field.reset());
    }

    public clear() {
        Object.values(this.fields).forEach(field => field.clear());
    }

    public validateAll() {
        Object.values(this.fields).forEach(field => field.validate());
        return !this.error;
    }

    public get fieldCount() {
        return Object.values(this.fields).filter(
            field => !field.isFieldDisabled
        ).length;
    }

    public get filledCount() {
        return Object.values(this.fields).filter(
            field => !field.isFieldDisabled && field.dirty
        ).length;
    }

    public equals(v1: Models<T>): boolean {
        const activeFields = Object.entries(this.fields)
            .filter(([, field]) => !field.isFieldDisabled)
            .map(([key]) => key);
        return EnumClass.create(v1)
            .getEntries()
            .filter(([key]) => activeFields.includes(key.toString()))
            .every(([key, value]) => {
                const assignedValue = this.model?.value?.[key];
                if (
                    ['string', 'number', 'undefined', 'null'].includes(
                        typeof value
                    )
                ) {
                    return assignedValue === value;
                }
                return true;
            });
    }
}
